import { createMemo, createSignal, For, Index, Show, type Accessor, type JSX } from 'solid-js';
import { Portal } from 'solid-js/web';
import Text from './Text';
import { useAnchoredMenu } from '../hooks/useAnchoredMenu';
import styles from './StorageSelect.module.css';

export function Highlight(props: { text: string; query: string }) {
    const matchStart = () => props.query ? props.text.toLowerCase().indexOf(props.query.toLowerCase()) : -1;
    return (
        <Show when={matchStart() >= 0} fallback={props.text}>
            {props.text.slice(0, matchStart())}
            <mark class={styles.match}>{props.text.slice(matchStart(), matchStart() + props.query.length)}</mark>
            {props.text.slice(matchStart() + props.query.length)}
        </Show>
    );
}

export interface PickerEmptyOption {
    label: string;
    icon: string;
}

interface ChoiceSection<T> {
    titleKey?: string;
    choices: T[];
}

export default function SearchableItemPicker<T>(props: {
    choices: T[];
    keyOf: (choice: T) => string;
    searchTextOf: (choice: T) => string;
    renderOption: (choice: T, query: Accessor<string>) => JSX.Element;
    renderSelected: (choice: T) => JSX.Element;
    sectionTitleKeyOf?: (choice: T) => string;
    value: string;
    onChange: (key: string) => void;
    placeholder: string;
    searchPlaceholder: string;
    noMatchKey: string;
    emptyOption?: PickerEmptyOption;
    disabled?: boolean;
}) {
    const menu = useAnchoredMenu(420);
    const [query, setQuery] = createSignal('');
    const [focusedKey, setFocusedKey] = createSignal<string | null>(null);
    const trimmedQuery = () => query().trim();

    const selected = () => props.choices.find(choice => props.keyOf(choice) === props.value);
    const matches = createMemo(() => {
        const words = trimmedQuery().toLowerCase().split(/\s+/).filter(word => word.length > 0);
        return props.choices.filter(choice => {
            const haystack = props.searchTextOf(choice).toLowerCase();
            return words.every(word => haystack.includes(word));
        });
    });
    const sections = createMemo<ChoiceSection<T>[]>(() => {
        const sectionTitleKeyOf = props.sectionTitleKeyOf;
        if (!sectionTitleKeyOf) return [{ choices: matches() }];
        const sectionsInOrder: ChoiceSection<T>[] = [];
        for (const choice of matches()) {
            const titleKey = sectionTitleKeyOf(choice);
            const existingSection = sectionsInOrder.find(section => section.titleKey === titleKey);
            if (existingSection) existingSection.choices.push(choice);
            else sectionsInOrder.push({ titleKey, choices: [choice] });
        }
        return sectionsInOrder;
    });
    const showEmptyOption = () => props.emptyOption !== undefined && !trimmedQuery();
    const focusableKeys = () => [
        ...(showEmptyOption() ? [''] : []),
        ...sections().flatMap(section => section.choices.map(props.keyOf)),
    ];
    const unavailable = () => props.disabled || (props.choices.length === 0 && props.emptyOption === undefined);

    let searchRef: HTMLInputElement | undefined;
    let listRef: HTMLDivElement | undefined;

    const openMenu = () => {
        if (unavailable()) return;
        setQuery('');
        setFocusedKey(focusableKeys().includes(props.value) ? props.value : (focusableKeys()[0] ?? null));
        menu.toggle();
        if (menu.open()) queueMicrotask(() => searchRef?.focus());
    };

    const choose = (key: string) => {
        menu.setOpen(false);
        if (key !== props.value) props.onChange(key);
    };

    const moveFocus = (step: number) => {
        const keys = focusableKeys();
        if (keys.length === 0) return;
        const currentIndex = keys.indexOf(focusedKey() ?? '');
        const nextKey = keys[Math.max(0, Math.min(keys.length - 1, currentIndex + step))];
        setFocusedKey(nextKey);
        listRef?.querySelector(`[data-choice-key="${CSS.escape(nextKey)}"]`)?.scrollIntoView({ block: 'nearest' });
    };

    const onSearchKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'ArrowDown') { event.preventDefault(); moveFocus(1); }
        else if (event.key === 'ArrowUp') { event.preventDefault(); moveFocus(-1); }
        else if (event.key === 'Enter') { event.preventDefault(); const key = focusedKey(); if (key !== null) choose(key); }
        else if (event.key === 'Escape') { event.preventDefault(); menu.setOpen(false); }
    };

    const onSearchInput = (value: string) => {
        setQuery(value);
        setFocusedKey(focusableKeys()[0] ?? null);
        queueMicrotask(menu.reposition);
    };

    const optionRow = (key: string, content: JSX.Element) => (
        <button
            type="button"
            role="option"
            data-choice-key={key}
            aria-selected={key === props.value}
            class={styles.option}
            classList={{ [styles.optionSelected]: key === props.value, [styles.optionFocused]: key === focusedKey() }}
            onMouseEnter={() => setFocusedKey(key)}
            onClick={() => choose(key)}
        >
            {content}
        </button>
    );

    return (
        <div class={styles.container} ref={menu.setContainerRef}>
            <button
                type="button"
                ref={menu.setTriggerRef}
                class={styles.trigger}
                classList={{ [styles.triggerOpen]: menu.open() }}
                disabled={unavailable()}
                aria-haspopup="listbox"
                aria-expanded={menu.open()}
                onClick={openMenu}
            >
                <Show
                    when={selected()}
                    keyed
                    fallback={
                        <Show when={!props.value && props.emptyOption} fallback={<span class={styles.placeholder}>{props.placeholder}</span>}>
                            {(emptyOption) => (
                                <>
                                    <i class={`fa-solid ${emptyOption().icon} ${styles.triggerIcon}`} aria-hidden="true" />
                                    <span class={styles.name}>{emptyOption().label}</span>
                                </>
                            )}
                        </Show>
                    }
                >
                    {(choice) => props.renderSelected(choice)}
                </Show>
                <i class={`fa-solid fa-chevron-down ${styles.chevron}`} classList={{ [styles.chevronOpen]: menu.open() }} aria-hidden="true" />
            </button>

            <Show when={menu.open()}>
                <Portal mount={document.body}>
                    <div
                        ref={menu.setMenuRef}
                        class={styles.dropdown}
                        role="listbox"
                        style={{
                            position: 'fixed',
                            left: `${menu.menuRect().left}px`,
                            top: `${menu.menuRect().top}px`,
                            width: `${menu.menuRect().width}px`,
                        }}
                    >
                        <label class={styles.search}>
                            <i class="fa-solid fa-magnifying-glass" aria-hidden="true" />
                            <input
                                ref={searchRef}
                                value={query()}
                                placeholder={props.searchPlaceholder}
                                onInput={(event) => onSearchInput(event.currentTarget.value)}
                                onKeyDown={onSearchKeyDown}
                            />
                        </label>
                        <div class={styles.list} ref={listRef}>
                            <Show when={showEmptyOption() && props.emptyOption}>
                                {(emptyOption) => optionRow('', (
                                    <>
                                        <span class={styles.lead}><i class={`fa-solid ${emptyOption().icon}`} aria-hidden="true" /></span>
                                        <span class={`${styles.name} ${styles.emptyOptionLabel}`}>{emptyOption().label}</span>
                                    </>
                                ))}
                            </Show>
                            <Index each={sections()}>
                                {(section) => (
                                    <>
                                        <Show when={section().titleKey}>
                                            {(titleKey) => (
                                                <div class={styles.sectionHeader}>
                                                    <Text val={titleKey()} />
                                                    <span class={styles.sectionCount}>{section().choices.length}</span>
                                                </div>
                                            )}
                                        </Show>
                                        <For each={section().choices}>
                                            {(choice) => optionRow(props.keyOf(choice), props.renderOption(choice, trimmedQuery))}
                                        </For>
                                    </>
                                )}
                            </Index>
                            <Show when={focusableKeys().length === 0}>
                                <div class={styles.noMatch}><Text val={props.noMatchKey} /></div>
                            </Show>
                        </div>
                    </div>
                </Portal>
            </Show>
        </div>
    );
}
